class CollectionInnerMenu extends HTMLElement {
  constructor() {
    super();

    this.prevArrowWidth = 0;
    this.debouncedCheckOverflow = debounce(this.checkOverflow.bind(this), 200);
    this.debouncedUpdateArrowWidth = debounce(this.updateArrowWidth.bind(this), 200);
    this.bindcheckOverflow = this.checkOverflow.bind(this);
    this.bindOnMouseDown = this.onMouseDown.bind(this);
    this.bindOnMouseUp = this.onMouseUp.bind(this);
    this.bindOnMouseMove = this.onMouseMove.bind(this);
    this.bindUpdateArrows = this.updateArrows.bind(this);
    this.bindScrollToNext = this.scrollToNextItem.bind(this);
    this.bindScrollToPrev = this.scrollToPrevItem.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute('swipe')) return;

    this.scrollContainer = this.querySelector('ul');
    this.items = this.querySelectorAll('a');

    if (!this.scrollContainer || !this.items) return;

    this.scrollContainer.scrollLeft = 0;
    this.handleScroll();
    this.checkOverflow();
    window.addEventListener('resize', this.bindcheckOverflow);

    this.prevArrow = this.querySelector('.collection-inner-menu__arrow--prev');
    this.nextArrow = this.querySelector('.collection-inner-menu__arrow--next');

    if (this.prevArrow && this.nextArrow) {
      this.updateArrowWidth();
      window.addEventListener('resize', this.debouncedUpdateArrowWidth);
      this.prevArrow.addEventListener('click', this.bindScrollToPrev);
      this.nextArrow.addEventListener('click', this.bindScrollToNext);
      this.scrollContainer.addEventListener('scroll', this.bindUpdateArrows);
      this.updateArrows();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.bindcheckOverflow);
    window.removeEventListener('resize', this.debouncedUpdateArrowWidth);
    this.prevArrow?.removeEventListener('click', this.bindScrollToPrev);
    this.nextArrow?.removeEventListener('click', this.bindScrollToNext);
    this.scrollContainer?.removeEventListener('scroll', this.bindUpdateArrows);
  }

  handleScroll() {
    this.isDown = false;
    this.startX = 0;
    this.initScrollLeft = this.scrollContainer.scrollLeft;

    this.scrollContainer.addEventListener('mousedown', this.bindOnMouseDown, { passive: false });
  }

  onMouseDown(event) {
    event.preventDefault();
    this.scrollContainer.classList.add('grabbing');
    this.isDown = true;
    this.hasMoved = false;
    this.startX = event.pageX - this.scrollContainer.offsetLeft;
    this.initScrollLeft = this.scrollContainer.scrollLeft;

    this.scrollContainer.addEventListener('mouseleave', this.bindOnMouseUp, { passive: false });
    this.scrollContainer.addEventListener('mouseup', this.bindOnMouseUp, { passive: false });
    this.scrollContainer.addEventListener('mousemove', this.bindOnMouseMove, { passive: false });
  }

  onMouseMove(event) {
    event.preventDefault();
    if (!this.isDown) return;
    const x = event.pageX - this.scrollContainer.offsetLeft;
    const walk = x - this.startX;
    if (Math.abs(walk) > 5) {
      this.scrollContainer.classList.add('moving');
      this.hasMoved = true;
    }
    this.scrollContainer.scrollLeft = this.initScrollLeft - walk;
  }

  onMouseUp() {
    this.isDown = false;
    this.hasMoved = false;
    this.scrollContainer.classList.remove('grabbing', 'moving');

    this.scrollContainer.removeEventListener('mouseleave', this.bindOnMouseUp, { passive: false });
    this.scrollContainer.removeEventListener('mouseup', this.bindOnMouseUp, { passive: false });
    this.scrollContainer.removeEventListener('mousemove', this.bindOnMouseMove, { passive: false });
  }

  checkOverflow() {
    const children = Array.from(this.scrollContainer.children);
    const childrenWidth = children.reduce((sum, child) => (sum + child.getBoundingClientRect().width), 0);
    const computedStyle = getComputedStyle(this.scrollContainer);
    const gap = parseFloat(computedStyle.columnGap || computedStyle.gap || 0);
    const gapWidth = (children.length - 1) * gap;
    const containerWidth = childrenWidth + gapWidth;

    if (this.scrollContainer.scrollWidth < containerWidth) {
      this.scrollContainer.classList.add('overflowed');
    } else {
      this.scrollContainer.classList.remove('overflowed');
    }

    this.updateArrows();
  }

  updateArrowWidth() {
    this.prevArrowWidth = this.prevArrow?.getBoundingClientRect().width ?? 0;
  }

  updateArrows() {
    if (!this.prevArrow || !this.nextArrow) return;
    const atStart = this.scrollContainer.scrollLeft <= 1;
    const atEnd = this.scrollContainer.scrollLeft + this.scrollContainer.clientWidth >= this.scrollContainer.scrollWidth - 1;
    this.prevArrow.classList.toggle('is-visible', !atStart);
    this.nextArrow.classList.toggle('is-visible', !atEnd);
  }

  scrollToNextItem() {
    const containerRect = this.scrollContainer.getBoundingClientRect();
    const items = [...this.scrollContainer.children];
    let anchorIndex = items.findIndex(item => item.getBoundingClientRect().left >= containerRect.left - 2);
    if (anchorIndex === -1) anchorIndex = items.length - 1;
    const target = items[Math.min(anchorIndex + 1, items.length - 1)];
    this.scrollContainer.scrollTo({
      left: this.scrollContainer.scrollLeft + target.getBoundingClientRect().left - containerRect.left - this.prevArrowWidth - 1,
      behavior: 'smooth'
    });
  }

  scrollToPrevItem() {
    const containerRect = this.scrollContainer.getBoundingClientRect();
    const items = [...this.scrollContainer.children];
    let anchorIndex = items.findIndex(item => item.getBoundingClientRect().left >= containerRect.left - 2);
    if (anchorIndex === -1) anchorIndex = items.length - 1;
    const targetIndex = Math.max(anchorIndex - 1, 0);
    if (targetIndex === 0) {
      this.scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    const target = items[targetIndex];
    this.scrollContainer.scrollTo({
      left: this.scrollContainer.scrollLeft + target.getBoundingClientRect().left - containerRect.left - this.prevArrowWidth - 1,
      behavior: 'smooth'
    });
  }
}

customElements.define('collection-inner-menu', CollectionInnerMenu);
